import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  model_used?: string;
  created_at: string;
  web_search_used?: boolean;
  files?: any;
}

export interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  messages?: Message[];
}

export const authAPI = {
  login: async (username: string, password: string) => {
    // Send as FormData (application/x-www-form-urlencoded)
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await axios.post(`${API_BASE_URL}/auth/login`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },
  
  register: async (username: string, email: string, password: string) => {
    const response = await api.post('/auth/register', { username, email, password });
    return response.data;
  },
  
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  getPendingUsers: async () => {
    const response = await api.get('/auth/pending');
    return response.data as { id: number; username: string; email: string }[];
  },

  approveUser: async (username: string) => {
    const response = await api.post(`/auth/approve/${encodeURIComponent(username)}`);
    return response.data;
  },

  rejectUser: async (username: string) => {
    const response = await api.post(`/auth/reject/${encodeURIComponent(username)}`);
    return response.data;
  },

  getApprovedUsers: async () => {
    const response = await api.get('/auth/approved');
    return response.data as { id: number; username: string; email: string; is_approved?: boolean }[];
  },

  deleteUser: async (username: string) => {
    const response = await api.delete(`/auth/users/${encodeURIComponent(username)}`);
    return response.data;
  },

  resetPassword: async (username: string, newPassword: string) => {
    const response = await api.post(`/auth/reset-password/${encodeURIComponent(username)}`, {
      new_password: newPassword,
    });
    return response.data;
  },
};

export const chatAPI = {
  sendMessage: async (message: string, conversationId?: number, mode: string = 'auto', files?: any[], model?: string) => {
    const response = await api.post('/chat', {
      message,
      conversation_id: conversationId,
      mode,
      files,
      ...(model && model !== 'default' ? { model } : {}),
    });
    return response.data;
  },
  
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get('/conversations');
    return response.data;
  },
  
  getConversation: async (id: number): Promise<Conversation> => {
    const response = await api.get(`/conversations/${id}`);
    return response.data;
  },
  
  deleteConversation: async (id: number) => {
    const response = await api.delete(`/conversations/${id}`);
    return response.data;
  },
  
  updateTitle: async (id: number, title: string) => {
    const response = await api.put(`/conversations/${id}/title?title=${encodeURIComponent(title)}`);
    return response.data;
  },
};

export const filesAPI = {
  upload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_BASE_URL}/files/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return response.data;
  },
};

export default api;
